import React from 'react';
import './Services.css';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaClipboardCheck, FaUserMd, FaLaptopMedical, FaFileAlt, FaComments, FaFlask, FaSearch, FaChalkboardTeacher, FaUserGraduate } from 'react-icons/fa';

type Package = {
  name: string;
  price: string;
};

type ServiceCardProps = {
  icon: React.ReactNode;
  title: string;
  price: string;
  packages?: Package[];
  description: string[];
  delay: number;
  comparable?: string;
  linkTo?: string;
  linkLabel?: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, price, packages, description, delay, comparable, linkTo, linkLabel }) => {
  return (
    <motion.div
      className="service-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}
    >
      <div className="service-icon">{icon}</div>
      <h3>{title}</h3>
      <div className="service-price">{price}</div>

      {packages && packages.length > 0 && (
        <div className="service-packages">
          {packages.map((pkg, i) => (
            <div key={i} className="package-item">
              <span className="package-name">{pkg.name}</span>
              <span className="package-price">{pkg.price}</span>
            </div>
          ))}
        </div>
      )}

      <ul className="service-bullets">
        {description.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>

      {linkTo && (
        <div className="service-link">
          <Link to={linkTo} className="btn btn-secondary btn-sm">{linkLabel || 'Learn More'}</Link>
        </div>
      )}

      {comparable && (
        <div className="service-comparable">
          Comparable large advising companies: {comparable}
        </div>
      )}
    </motion.div>
  );
};

type ServiceGroup = {
  label: string;
  services: ServiceCardProps[];
};

const Services: React.FC = () => {
  const groups: ServiceGroup[] = [
    {
      label: "Writing & Editing",
      services: [
        {
          icon: <FaFileAlt />,
          title: "Personal Statement Review",
          price: "From $49",
          delay: 0,
          packages: [
            { name: "First Round of Editing", price: "$49" },
            { name: "Each Additional Round", price: "$29" },
          ],
          description: [
            "Comprehensive written feedback",
            "Tracked edits and comments",
            "Suggestions on structure, tone, and content",
            "Turnaround time: 1-2 business days"
          ],
          comparable: "$200\u2013$500",
        },
        {
          icon: <FaClipboardCheck />,
          title: "Activities Section Review",
          price: "From $19",
          delay: 1,
          packages: [
            { name: "Single Activity", price: "$19" },
            { name: "Most Meaningful Activity", price: "$39" },
            { name: "Full Section", price: "$179" },
          ],
          description: [
            "Includes one comprehensive review round",
            "Optimization of descriptions",
            "Suggestions on structure, tone, and content",
            "Additional full review round: $50"
          ],
          comparable: "$400\u2013$1,000",
        },
        {
          icon: <FaGraduationCap />,
          title: "Secondary Essay Edits",
          price: "From $19 per essay",
          delay: 2,
          packages: [
            { name: "\u2264100 words", price: "$19" },
            { name: "100 to 500 words", price: "$35" },
            { name: ">500 words", price: "$45" },
          ],
          description: [
            "1 comprehensive review + 1 revision pass per essay",
            "Detailed tracked edits and personalized feedback",
            "School-specific positioning feedback",
            "Cross-essay narrative alignment",
            "Guidance on answering school-specific prompts effectively",
          ],
          comparable: "$75\u2013$200",
        },
        {
          icon: <FaGraduationCap />,
          title: "Secondary Essay Bundles",
          price: "From $149",
          delay: 3,
          packages: [
            { name: "5 Essays", price: "$149" },
            { name: "10 Essays", price: "$289" },
            { name: "20 Essays", price: "$549" },
            { name: "30 Essays", price: "$749" },
          ],
          description: [
            "Priority turnaround for bundle clients",
            "Limited July/August availability",
            "Custom bundles and upgrades available"
          ],
          linkTo: "/contact",
          linkLabel: "Reserve Now",
        },
      ],
    },
    {
      label: "Advising & Strategy",
      services: [
        {
          icon: <FaComments />,
          title: "Hourly Advising",
          price: "$79/hour",
          delay: 0,
          description: [
            "Flexible, pay-as-you-go support for any part of the application process",
            "Feedback on personal statement, activities, and secondaries",
            "Interview preparation (traditional or MMI)",
            "General guidance on strategy, pre-med activities, research, etc.",
            "Application timeline or school list advising"
          ],
          comparable: "$150\u2013$300",
        },
        {
          icon: <FaUserMd />,
          title: "Mock Interview",
          price: "$79",
          delay: 1,
          description: [
            "One 60-minute session (traditional or MMI style)",
            "Personalized verbal and written feedback",
            "Practice on common and challenging questions"
          ],
          comparable: "$150\u2013$400",
        },
        {
          icon: <FaSearch />,
          title: "Application Audit & Reapplicant Assessment",
          price: "$189",
          delay: 2,
          description: [
            "Comprehensive review of the entire application",
            "Personal statement assessment",
            "Activities section evaluation",
            "School list analysis",
            "Identification of application strengths and weaknesses",
            "Written action plan with prioritized recommendations",
            "45-minute strategy call"
          ],
        },
      ],
    },
    {
      label: "Medical School Application Packages",
      services: [
        {
          icon: <FaLaptopMedical />,
          title: "Essentials Package",
          price: "$499",
          delay: 0,
          description: [
            "One 1-hour advising session",
            "Personal statement review (up to 3 rounds of edits)",
            "Work & Activities section (up to 3 rounds of edits)",
            "Up to 3 secondary essay reviews",
            "Tailored school list guidance",
            "Email/text support"
          ],
          comparable: "$800\u2013$2,000+",
        },
        {
          icon: <FaLaptopMedical />,
          title: "Comprehensive Package",
          price: "$1199",
          delay: 1,
          description: [
            "Three 1-on-1 advising sessions",
            "Comprehensive personal statement editing (multiple rounds)",
            "Complete Work & Activities section development and editing",
            "Full AMCAS/AACOMAS application review and optimization",
            "Tailored school list development",
            "Up to 10 secondary essay reviews",
            "Two mock interviews (traditional or MMI style)",
            "Email/text support"
          ],
          comparable: "$2,500\u2013$6,000+",
        },
      ],
    },
    {
      label: "BS/MD Packages",
      services: [
        {
          icon: <FaGraduationCap />,
          title: "BS/MD Essentials Package",
          price: "$649",
          delay: 0,
          description: [
            "One hour of advising on Zoom",
            "Common App personal essay review (up to 3 rounds of edits)",
            "Activities and Honors section review (up to 3 rounds of edits)",
            "Up to 3 program supplement reviews",
            "BS/MD program list guidance",
            "Email/text support"
          ],
        },
        {
          icon: <FaUserGraduate />,
          title: "BS/MD Comprehensive Package",
          price: "$1,599",
          delay: 1,
          description: [
            "Three hours of advising on Zoom",
            "Comprehensive Common App personal essay editing (multiple rounds)",
            "Complete Activities and Honors section development and editing",
            "Full application review and optimization across the Common App and program portals",
            "Tailored BS/MD and backup college list development",
            "Up to 10 program supplement reviews",
            "Parent Zoom session",
            "Two mock interviews (traditional or MMI style)",
            "Email/text support"
          ],
        },
      ],
    },
    {
      label: "Premed Mentorship",
      services: [
        {
          icon: <FaChalkboardTeacher />,
          title: "Premed Mentorship Lite",
          price: "Starting at $89/month",
          delay: 0,
          description: [
            "One 30-minute Zoom session per month",
            "Email/text support",
            "Course and semester planning",
            "Guidance on extracurriculars, research, shadowing, and volunteering",
            "Goal setting and accountability"
          ],
        },
        {
          icon: <FaUserGraduate />,
          title: "Premed Mentorship Plus",
          price: "Starting at $139/month",
          delay: 1,
          description: [
            "One 60-minute Zoom session per month",
            "Ongoing email/text support",
            "Everything included in Mentorship Lite",
            "Personalized long-term premed roadmap",
            "Research and extracurricular strategy",
            "Resume/CV review",
            "Priority scheduling",
            "Parent check-ins if desired"
          ],
        },
      ],
    },
    {
      label: "Research Support",
      services: [
        {
          icon: <FaFlask />,
          title: "Medical Research Support",
          price: "Pricing varies",
          delay: 0,
          description: [
            "One-on-one collaboration with an experienced medical student researcher",
            "Research topic and question development",
            "Study design, literature review, and manuscript structure guidance",
            "Section-by-section writing feedback and line-by-line editing",
            "Submission-ready manuscript/poster preparation support"
          ],
          linkTo: "/research",
          linkLabel: "See Research Support",
        },
      ],
    },
  ];

  return (
    <section id="services" className="services-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2>Our Services</h2>
          <p>Comprehensive support for your medical school journey</p>
        </motion.div>

        {groups.map((group, gi) => (
          <div key={gi} className="service-group">
            <motion.div
              className="service-group-header"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="service-group-label">{group.label}</h3>
              <div className="service-group-divider" />
            </motion.div>
            <div className="services-grid">
              {group.services.map((service, si) => (
                <ServiceCard
                  key={si}
                  icon={service.icon}
                  title={service.title}
                  price={service.price}
                  description={service.description}
                  delay={service.delay}
                  packages={service.packages}
                  comparable={service.comparable}
                  linkTo={service.linkTo}
                  linkLabel={service.linkLabel}
                />
              ))}
            </div>
          </div>
        ))}

        <motion.div
          className="pricing-note"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginTop: '2rem', fontStyle: 'italic', color: '#666' }}
        >
          <p>Need something different? Custom packages are available during your free consultation.</p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
